import {useEffect, useRef, useState} from 'react'
import {Link, useSearchParams} from 'react-router-dom'
import {toAbsoluteUrl} from '@/shared/utils'
import {getLogger} from '../../../../shared/utils/logUtils'
import {confirmCognitoSignUp, resendCognitoSignUp} from '../core/_requests'

const DESKTOP_URL = 'https://www.biobam.com/download-omicsbox/'
const ACCOUNT_URL = 'https://account.biobam.com'

type Status = 'verifying' | 'verified' | 'expired' | 'invalid'

interface CognitoError {
  code?: string
  name?: string
  message?: string
}

function statusForError(error: CognitoError): Status {
  const code = error.code ?? error.name
  // A second click on the same email link: the account is already usable.
  if (code === 'NotAuthorizedException' && /CONFIRMED/i.test(error.message ?? '')) {
    return 'verified'
  }
  if (code === 'ExpiredCodeException') {
    return 'expired'
  }
  return 'invalid'
}

const ProductLinks = () => (
  <>
    <a href={DESKTOP_URL} target='_blank' rel='noopener noreferrer'>
      OmicsBox
    </a>
    , <Link to='/'>OmicsBox Web</Link> and your{' '}
    <a href={ACCOUNT_URL} target='_blank' rel='noopener noreferrer'>
      BioBam Account
    </a>
  </>
)

const ExpiredCode = ({userName}: {userName: string}) => {
  const [resend, setResend] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle')

  const handleResend = async () => {
    setResend('sending')
    try {
      await resendCognitoSignUp(userName)
      setResend('sent')
    } catch (error) {
      getLogger().error(error)
      setResend('failed')
    }
  }

  return (
    <>
      <h4 className='mb-4'>This verification link has expired</h4>
      {resend === 'sent' ? (
        <p>We have sent you a new verification email. Please click Verify in that email.</p>
      ) : (
        <>
          <p>Request a new verification email and click Verify in it.</p>
          {resend === 'failed' && (
            <p className='errorMessage-customizable'>
              The email could not be sent. Please try again later.
            </p>
          )}
          <button
            type='button'
            className='btn btn-primary submitButton-customizable'
            onClick={handleResend}
            disabled={resend === 'sending'}
          >
            {resend === 'sending' ? 'Sending...' : 'Send a new verification email'}
          </button>
        </>
      )}
    </>
  )
}

export const ConfirmRegistration = () => {
  const [params] = useSearchParams()
  const userName = params.get('u') ?? ''
  const code = params.get('code') ?? ''
  const email = params.get('e') ?? ''
  const [status, setStatus] = useState<Status>(userName && code ? 'verifying' : 'invalid')
  const started = useRef(false)

  useEffect(() => {
    // StrictMode runs effects twice; one confirmation per page load is enough.
    if (!userName || !code || started.current) return
    started.current = true
    confirmCognitoSignUp(userName, code)
      .then(() => setStatus('verified'))
      .catch((error: CognitoError) => {
        getLogger().error(error)
        setStatus(statusForError(error))
      })
  }, [userName, code])

  return (
    <div className='modal-dialog-cognito'>
      <div className='modal-content background-customizable modal-content-mobile'>
        <div className='text-center banner-customizable'>
          <img alt='Logo' src={toAbsoluteUrl('/media/logos/ob_web.png')} className='h-95px' />
        </div>
        <div className='modal-body'>
          <br />
          {status === 'verifying' && (
            <div className='text-center mb-5'>
              <span className='spinner-border spinner-border-sm align-middle me-2' />
              Verifying your email address...
            </div>
          )}

          {status === 'verified' && (
            <>
              <h4 className='mb-4'>Your email address has been verified</h4>
              <p>
                You can now use {email ? <strong>{email}</strong> : 'this email address'} to log in
                to <ProductLinks />.
              </p>
              <Link to='/signin' className='btn btn-primary submitButton-customizable mt-4'>
                Go to Sign In
              </Link>
            </>
          )}

          {status === 'expired' && <ExpiredCode userName={userName} />}

          {status === 'invalid' && (
            <>
              <h4 className='mb-4'>This verification link is not valid</h4>
              <p>
                Please use the Verify button from the most recent verification email. If your
                address is already verified, you can simply sign in.
              </p>
              <Link to='/signin' className='btn btn-primary submitButton-customizable mt-4'>
                Go to Sign In
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
